import agent from "../agent";
import profileStore from "../stores/profileStore";
import { ProfileType } from "../types/enum";

const sameId = (left, right) => left != null && right != null && String(left) === String(right);

function requireArray(response) {
    if (!Array.isArray(response)) {
        throw new Error("Unable to load the complete venue agent roster. Please try again.");
    }
    return response;
}

// Limit simultaneous venue reads rather than flooding the API for large organisations.
async function readVenues(venues, read) {
    const results = [];
    for (let offset = 0; offset < venues.length; offset += 4) {
        results.push(...await Promise.all(venues.slice(offset, offset + 4).map(read)));
    }
    return results.reduce((all, rows) => all.concat(rows), []);
}

function uniqueRows(rows) {
    const seen = new Set();
    return rows.filter(row => {
        if (row.id == null) throw new Error("A venue agent record is missing its relationship ID.");
        const key = String(row.id);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
}

export async function loadStaffAgentVenues() {
    // This is the same permission-scoped source used by staff reporting filters.
    const response = requireArray(await agent.Venue.getUserVenuesBasedOnPermissionSimple());
    const seen = new Set();
    const venues = response.filter(venue => {
        if (venue.id == null) throw new Error("A venue is missing its profile ID.");
        const key = String(venue.id);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
    await Promise.all(venues.map(venue => profileStore.mapAdditionalProfileDataAsync(venue)));
    return venues.sort((left, right) =>
        (left.fullName || left.profileName || "").localeCompare(right.fullName || right.profileName || "")
    );
}

export async function loadStaffVenueAgents(venues) {
    const rows = await readVenues(venues, async venue => {
        const response = requireArray(await agent.AgentVenues.getMyVenuesAgent(venue.id));
        return Promise.all(response.map(async data => {
            const venueId = data.venueId == null ? (data.venue?.id || venue.id) : data.venueId;
            if (!sameId(venueId, venue.id)) {
                throw new Error("The agent roster contains a relationship for another venue.");
            }
            await profileStore.mapAdditionalProfileDataAsync(data.agent);
            return { ...data, venueId, venue: { ...venue, ...data.venue }, accepted: true };
        }));
    });
    return uniqueRows(rows);
}

export async function loadStaffVenueAgentRequests(venues) {
    const rows = await readVenues(venues, async venue => {
        const response = requireArray(await agent.ProfileRelationship.getVenueAgentRequest(venue.id));
        return Promise.all(response.map(async data => {
            const venueProfile = data.primaryProfile?.type === ProfileType.Venue.ordinal
                ? data.primaryProfile : data.secondaryProfile;
            const venueId = venueProfile?.id || (data.primaryProfile?.type === ProfileType.Venue.ordinal
                ? data.primaryProfileId : data.secondaryProfileId);
            if (!sameId(venueId, venue.id)) {
                throw new Error("The agent requests contain a relationship for another venue.");
            }
            await profileStore.mapAdditionalProfileDataAsync(data.primaryProfile);
            await profileStore.mapAdditionalProfileDataAsync(data.secondaryProfile);
            return { ...data, accepted: true };
        }));
    });
    return uniqueRows(rows);
}